import * as Api from '@ViewModels';
import { IImpersonationContext, Industry, TemplateStatus } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { parse as queryStringToObject } from 'query-string';
import * as React from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { DefaultSupportedIndustries } from '../../../../models';
import { ISystemAutomationTemplate } from '../../../../models/AdminModels';
import {
	EditAutomationTemplateStepsContext,
	IEditAutomationTemplateStepsContext,
} from '../../../../models/Automations';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import {
	useArchiveIndustryAutomationTemplateMutation,
	useIndustryAutomationTemplateQuery,
	usePublishIndustryAutomationTemplateMutation,
	useSetIndustryAutomationTemplateIndustriesMutation,
} from '../../../../queries/Admin';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { EditAutomationTemplate } from '../../../../web/containers/EditAutomationTemplate';
import { INavigationItemProps } from '../../../../web/containers/MainContainer';
import { baseStyleSheet } from '../../../../web/styles/styles';
import {
	DefaultIndustryDropdownOptions,
	IndustriesDropdown,
	TemplateIndustryOption,
} from '../../../components/IndustriesDropdown';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const EditIndustryAutomationTemplate: React.FC<IProps> = props => {
	const { className, styles = [], location, ...restProps } = props;

	const params = useParams<{ id: string; accountId?: string }>();
	const templateId = params.id;

	// Industry templates can be edited in a purely admin context (no account), but some API calls may still require
	// impersonation when this screen is accessed from within an account route.
	const impersonationContext: IImpersonationContext | undefined = React.useMemo(() => {
		return params.accountId ? { account: { id: params.accountId } } : undefined;
	}, [params.accountId]);

	const industryAutomationTemplateQuery = useIndustryAutomationTemplateQuery({ id: templateId });
	const industryAutomationTemplate = industryAutomationTemplateQuery.data ?? null;
	const { logEvent, logApiError } = useEventLogging('EditIndustryAutomationTemplate');
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const { postNotification } = useLocalNotificationService<ISystemAutomationTemplate>();

	const [draftUpdated, setDraftUpdated] = React.useState<boolean>(false);
	const [selectedIndustry, setSelectedIndustry] = React.useState<Industry>(null);
	const [selectedIndustryOptions, setSelectedIndustryOptions] = React.useState<ISelectOption<TemplateIndustryOption>[]>(
		[DefaultIndustryDropdownOptions.find(x => x.dataContext === 'All')]
	);

	const closeModal = () => {
		fullscreenModal?.history?.goBackToLocationBeforeTrackedHistory?.(true);
	};

	const publishMutation = usePublishIndustryAutomationTemplateMutation({
		onError: error => {
			errorMessages?.pushApiError(error);
			logApiError('Publish-Error', error);
		},
		onSuccess: data => {
			setDraftUpdated(false);
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
			closeModal();
		},
	});

	const archiveMutation = useArchiveIndustryAutomationTemplateMutation({
		onError: error => {
			errorMessages?.pushApiError(error);
			logApiError('Archive-Error', error);
		},
		onSuccess: data => {
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
			closeModal();
		},
	});

	const setIndustriesMutation = useSetIndustryAutomationTemplateIndustriesMutation({
		onError: error => {
			errorMessages?.pushApiError(error);
			logApiError('SetIndustries-Error', error);
		},
		onSuccess: data => {
			setDraftUpdated(false);
			postNotification({
				info: data,
				topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
			});
			closeModal();
		},
	});

	React.useEffect(() => {
		if (!industryAutomationTemplate) {
			return;
		}

		const queryParams = queryStringToObject(location?.search || '');
		const industries = new Set<Industry>(Object.keys(industryAutomationTemplate.industries || {}) as Industry[]);
		const selectedOptions = DefaultIndustryDropdownOptions.filter(x => industries.has(x.dataContext as Industry));

		setSelectedIndustry(queryParams?.industry as Industry);
		setSelectedIndustryOptions(
			selectedOptions.length > 0 ? selectedOptions : [DefaultIndustryDropdownOptions.find(x => x.dataContext === 'All')]
		);
	}, [industryAutomationTemplate, location?.search]);

	const publish = React.useCallback(() => {
		if (!industryAutomationTemplate) {
			return;
		}

		if (selectedIndustry) {
			logEvent('Publish', {
				industry: selectedIndustry,
				templateId: industryAutomationTemplate.template.id,
			});
			publishMutation.mutate({
				templateId: industryAutomationTemplate.template.id,
				industry: selectedIndustry,
				automationTemplate: industryAutomationTemplate.template,
			});
		} else {
			const industries = Object.keys(industryAutomationTemplate.industries || {}) as Industry[];
			logEvent('PublishForIndustries', { industries });
			setIndustriesMutation.mutate({
				templateId: industryAutomationTemplate.template.id,
				industries,
				status: TemplateStatus.Published,
				automationTemplate: industryAutomationTemplate.template,
			});
		}
	}, [industryAutomationTemplate, selectedIndustry, logEvent, publishMutation, setIndustriesMutation]);

	const archive = React.useCallback(() => {
		if (!industryAutomationTemplate || !selectedIndustry) {
			return;
		}

		logEvent('Archive', {
			industry: selectedIndustry,
			templateId: industryAutomationTemplate.template.id,
		});
		archiveMutation.mutate({
			templateId: industryAutomationTemplate.template.id,
			industry: selectedIndustry,
			automationTemplate: industryAutomationTemplate.template,
		});
	}, [industryAutomationTemplate, selectedIndustry, logEvent, archiveMutation]);

	const onSelectedIndustryOptionsChanged = React.useCallback(
		(nextSelectedIndustryOptions: ISelectOption<TemplateIndustryOption>[]) => {
			if (!industryAutomationTemplate) {
				return;
			}

			const industries =
				nextSelectedIndustryOptions[0].dataContext === 'All'
					? DefaultSupportedIndustries
					: (nextSelectedIndustryOptions.map(x => x.dataContext) as Industry[]);

			setSelectedIndustryOptions(nextSelectedIndustryOptions);

			logEvent('SetIndustries', { industries });
			setIndustriesMutation.mutate(
				{
					templateId: industryAutomationTemplate.template.id,
					industries,
					status: TemplateStatus.Draft,
					automationTemplate: industryAutomationTemplate.template,
				},
				{
					onError: () => {
						setSelectedIndustryOptions(selectedIndustryOptions);
					},
				}
			);
		},
		[industryAutomationTemplate, selectedIndustryOptions, logEvent, setIndustriesMutation]
	);

	const onRenderHeader = React.useCallback(() => {
		if (!industryAutomationTemplate) {
			return null;
		}

		const status = selectedIndustry ? industryAutomationTemplate.industries[selectedIndustry] : TemplateStatus.Draft;

		const nodes: React.ReactNode[] = [];
		const publishButton = (
			<button className={css(styleSheet.publishButton)} key='publish' onClick={publish}>
				<span>Publish</span>
			</button>
		);

		switch (status) {
			case TemplateStatus.Published: {
				const template = industryAutomationTemplate.template;
				const hasDraft = 'draft' in template ? template.draft : template.draftSteps || template.draftTrigger;
				if (hasDraft || draftUpdated) {
					nodes.push(publishButton);
				}
				nodes.push(
					<button className={css(baseStyleSheet.ctaButton)} key='archive' onClick={archive}>
						<span>Archive</span>
					</button>
				);
				break;
			}
			case TemplateStatus.Draft: {
				nodes.push(publishButton);
				break;
			}
			default: {
				break;
			}
		}

		return (
			<>
				<IndustriesDropdown
					onSelectedIndustryOptionsChanged={onSelectedIndustryOptionsChanged}
					selectedIndustryOptions={selectedIndustryOptions}
				/>
				{nodes}
			</>
		);
	}, [
		industryAutomationTemplate,
		selectedIndustry,
		draftUpdated,
		selectedIndustryOptions,
		publish,
		archive,
		onSelectedIndustryOptionsChanged,
	]);

	const [unsavedStep, setUnsavedStep] = React.useState<Api.IAutomationStep>(null);
	const editAutomationTemplateStepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(() => {
		return {
			automationTemplateId: industryAutomationTemplate?.template?.id,
			impersonationContext,
			unsavedStep,
			setUnsavedStep,
			isAdminContext: true,
		};
	}, [industryAutomationTemplate?.template?.id, impersonationContext, unsavedStep]);

	if (!industryAutomationTemplate) {
		return (
			<div className={`${css(styleSheet.container, ...styles)} edit-industry-automation-template ${className || ''}`}>
				{industryAutomationTemplateQuery.isError ? (
					<div className={css(baseStyleSheet.absoluteCenter)}>
						<div>Failed to load automation template.</div>
					</div>
				) : (
					<LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />
				)}
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container, ...styles)} edit-industry-automation-template ${className || ''}`}>
			<EditAutomationTemplateStepsContext.Provider value={editAutomationTemplateStepsContext}>
				<EditAutomationTemplate
					{...restProps}
					onRenderHeader={onRenderHeader}
					readonly={
						selectedIndustry
							? industryAutomationTemplate.industries[selectedIndustry] === TemplateStatus.Archived
							: undefined
					}
					onAutomationDeleted={closeModal}
					onAutomationPublished={closeModal}
					onAutomationDisabled={closeModal}
				/>
			</EditAutomationTemplateStepsContext.Provider>
		</div>
	);
};
